@if (loaded && selectedReq) {
  <div class="flex flex-col md:flex-row flex-1">
    <aside
      class="w-full md:w-64 bg-white shadow-md overflow-y-auto flex-shrink-0 border-gray-200 border-r requests-bar"
    >
      <div class="px-3 py-2 border-b border-gray-200">
        <h2 class="text-base font-semibold text-gray-700 flex items-center gap-2">
          Requests found
        </h2>
      </div>
      <div class="px-3 py-2 space-y-2">
        <div class="space-y-1">
          @if (agRealm === 'admins') {
            <div class="text-sm py-1.5 border-t border-b border-gray-200">
              <a (click)="toggleDownloadList()" class="cursor-pointer hover:underline text-primary">
                Download request/response XML
              </a>

              <ul [hidden]="!showDownloadList" class="space-y-1 mt-1 ml-3">
                <!-- Only Provider -->
                @for (provider of providers; let idx = $index; track provider) {
                  <li class="flex items-center gap-1.5 relative">
                    <a
                      class="hover:underline text-primary"
                      [class.cursor-pointer]="!zipDownloading"
                      [class.pointer-events-none]="zipDownloading"
                      [class.opacity-50]="zipDownloading"
                      (click)="
                        zipDownloading ? null : downloadZip(provider, 'only-provider', idx + 100)
                      "
                    >
                      {{ provider }}
                    </a>
                    @if (zipDownloading && currentIdx === idx + 100) {
                      <span class="loading loading-spinner loading-xs text-primary"></span>
                    }
                  </li>
                }

                <!-- All -->
                @for (provider of providers; let idx = $index; track provider) {
                  <li class="flex items-center gap-1.5 relative">
                    <a
                      class="hover:underline text-primary"
                      [class.cursor-pointer]="!zipDownloading"
                      [class.pointer-events-none]="zipDownloading"
                      [class.opacity-50]="zipDownloading"
                      (click)="zipDownloading ? null : downloadZip(provider, 'test', idx)"
                    >
                      {{ provider }}-test
                    </a>
                    @if (zipDownloading && currentIdx === idx) {
                      <span class="loading loading-spinner loading-xs text-primary"></span>
                    }
                  </li>
                }
              </ul>
            </div>
          }
          <div class="text-sm">
            <strong>consumer:</strong>
            {{ requests[0]?.consumer }}
          </div>
          <!--
            The "Is Important" toggle has moved out of this rail and
            into the session strip header at the top of the main
            column (search for ri-star). Putting it next to the
            session metadata keeps session-scoped controls together.
          -->
        </div>

        <!--
          No max-height here. The rail GROWS as chunks append, and the
          scroll belongs to the .requests-bar aside above; capping this
          box at 100vh (as a previous max-h-screen did) leaves the
          overflowing tiles painting over the sentinel and footer below
          once the list passes one viewport.
        -->
        <div class="space-y-1.5">
          @for (request of requests; let idx = $index; track request.id) {
            <!--
              Request tile styling — two orthogonal channels:

                STATUS  → status colour scale from getStatus pipe
                          (ok = white, warning = amber, error = red).
                          Appears as the tile background tint.

                STATE   → selection (active in the right pane) vs. not.
                          Appears as a black ring on top of any status
                          tint, layout-neutral so toggling doesn't
                          shift the row.

              Errored-and-selected reads as red bg + black ring;
              healthy-and-selected reads as white bg + black ring;
              errored-and-idle reads as red bg + red border, etc.
              Same status palette is reused on the home table rows
              and the request/trace pills via the StatusInfo type.
            -->
            <div
              class="px-2 py-1.5 rounded border cursor-pointer transition"
              [ngClass]="[
                (request | getStatus).tile,
                selectedReq?.id === request.id ? 'ring-2 ring-gray-900 border-gray-900' : '',
              ]"
              (click)="selectReq(request)"
            >
              <div class="flex justify-between items-center gap-2 min-w-0">
                <span
                  class="text-sm truncate min-w-0"
                  [ngClass]="request | getMethodColor"
                  [class.font-medium]="selectedReq?.id === request.id"
                  [title]="request | getMethodLabel"
                >
                  {{ request | getMethodLabel }}
                </span>
                <span class="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
                  {{ request | getProviders }}
                </span>
              </div>
              <!--
                Provider-callback flag. OrderChangeNotif is pushed TO us
                by the provider and merely acknowledged — it is not a
                call made through our platform. Surface that inline so an
                operator scanning the rail isn't misled into reading it
                as a consumer/our-API request. Distinct indigo chip — does
                NOT touch the tile bg (STATUS) or ring (STATE) channels
                documented above.
              -->
              @if (request | isProviderCallback) {
                <div
                  class="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 whitespace-nowrap"
                  title="Provider-initiated callback — pushed to us by the provider and acknowledged by our platform. Not a request made through our API."
                >
                  <i class="ri-arrow-left-down-line"></i>
                  Provider Callback
                </div>
              }
              <div class="text-xs text-gray-500 mt-1">
                {{ request | jsonApiTraceTime }}
              </div>
            </div>
          }
        </div>

        <!--
          Incremental load sentinel for the request rail.

          The rail appends oldest-first in SESSION_PAGE_SIZE chunks, so
          scrolling down moves forward in time and each chunk lands after
          the one before it: this
          block sits below the last tile, and an IntersectionObserver
          rooted on the scrolling .requests-bar (NOT the viewport) fetches
          the next chunk as it comes into range. Absent entirely once the
          session is fully loaded, so a session that fits in one chunk —
          the overwhelming majority — shows nothing here at all.

          The button is a deliberate fallback, not decoration: it is the
          only way to advance by keyboard, and it covers the case where
          the observer does not fire because the rail is short enough
          that the sentinel never leaves view.
        -->
        @if (hasMoreRequests) {
          <div #railSentinel class="mt-2 pt-2 border-t border-gray-200" data-testid="rail-sentinel">
            @if (pageLoading) {
              <div class="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
                <i class="ri-loader-4-line animate-spin"></i>
                Loading more…
              </div>
            } @else {
              <button
                type="button"
                class="w-full px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                (click)="loadMoreRequests()"
              >
                Load more
              </button>
            }
          </div>
        }

        <!--
          Rail footer. Only meaningful once the session spans more than
          one chunk, so it follows the same visibility rule as the
          sentinel plus the fully-loaded case, which keeps a final count
          on screen after the sentinel has gone.
        -->
        @if (requestsTotal > perPage) {
          <div class="mt-2 text-xs text-gray-500 tabular-nums text-center" data-testid="rail-count">
            {{ requests.length }} of {{ requestsTotal }} requests
          </div>
        }
      </div>
    </aside>

    <div class="flex-1 flex flex-col">
      <div class="px-3 py-2 border-b border-gray-200 bg-white">
        <div class="flex items-center justify-between gap-4">
          <button
            class="text-primary hover:underline text-sm flex items-center whitespace-nowrap !rounded-button"
            (click)="location.back()"
          >
            <i class="ri-arrow-go-back-fill mr-1"></i>
            Back
          </button>
          <div class="flex gap-2">
            @if (agRealm === 'admins') {
              <!--
                Launch in BP — session-level. Rebuilds this session's
                AirShopping RQ into a BookingPad /search deep-link and
                opens it in the chosen environment. Hidden when the
                session has no AirShopping request (nothing to replay).
              -->
              @if (airShoppingRequest) {
                <div class="dropdown dropdown-end">
                  <button
                    tabindex="0"
                    class="btn btn-sm btn-outline btn-secondary whitespace-nowrap"
                    [disabled]="relaunchPending"
                    title="Open this session's AirShopping search in BookingPad"
                  >
                    Launch in BP
                    <i class="ri-arrow-down-s-line ml-1"></i>
                  </button>
                  <ul
                    tabindex="0"
                    class="dropdown-content menu bg-base-100 rounded-box z-10 mt-1 w-52 p-2 shadow"
                  >
                    @for (t of bpTargets; track t.label) {
                      <li>
                        <a (click)="relaunchInBP(t.base)">{{ t.label }}</a>
                      </li>
                    }
                    <!--
                      Localhost row — its own entry because the dev port
                      is operator-configurable. The inline input writes
                      through to localStorage (onBpPortChange); the link
                      uses the live bpLocalhostBase. stopPropagation on
                      the input so editing the port doesn't trigger the
                      menu's click-to-close / the relaunch link.
                    -->
                    <li>
                      <div class="flex items-center gap-2 px-1">
                        <a class="flex-1 cursor-pointer" (click)="relaunchInBP(bpLocalhostBase)">
                          Localhost
                        </a>
                        <span class="text-xs text-gray-400">:</span>
                        <input
                          type="number"
                          min="1"
                          max="65535"
                          class="input input-xs input-bordered w-16"
                          [value]="bpLocalPort"
                          (change)="onBpPortChange($event)"
                          (click)="$event.stopPropagation()"
                          (keydown.enter)="onBpPortChange($event)"
                          title="Local BookingPad dev port — saved in this browser (default 4201)"
                        />
                      </div>
                    </li>
                  </ul>
                </div>
              }
              <button
                class="btn btn-sm btn-outline"
                (click)="openCompare()"
                title="Compare this session against another — request-by-request payload diff"
              >
                <i class="ri-git-compare-line mr-1"></i>
                Compare with…
              </button>
              <button
                class="btn btn-sm btn-secondary"
                (click)="openReplay()"
                title="Repeat this session against the live API — admin-only"
              >
                <i class="ri-restart-line mr-1"></i>
                Replay
              </button>
              <button
                class="btn btn-sm btn-primary"
                (click)="openEvaluation()"
                title="Run an AI evaluation of this session's traces"
              >
                <i class="ri-sparkling-line mr-1"></i>
                AI Evaluation
              </button>
              <button
                class="btn btn-sm btn-outline btn-primary"
                (click)="openTicket()"
                title="Generate a structured support/engineering ticket from this session"
              >
                <i class="ri-ticket-2-line mr-1"></i>
                AI Ticket
              </button>
            }
          </div>
        </div>
      </div>
      @if (showEvaluationPanel) {
        <app-evaluation-panel
          [evaluation]="evaluation"
          [loading]="loadingEvaluation"
          [error]="evaluationError"
          (closePanel)="closeEvaluation()"
          (reevaluate)="reevaluate()"
        ></app-evaluation-panel>
      }
      @if (showTicketPanel) {
        <app-ticket-panel
          [sessionValue]="session?.value ?? ''"
          (closePanel)="closeTicket()"
        ></app-ticket-panel>
      }
      @if (showReplayPanel) {
        <app-replay-panel
          [sessionValue]="session?.value ?? ''"
          (closePanel)="closeReplay()"
        ></app-replay-panel>
      }
      @if (showDiffInputModal) {
        <app-diff-input-modal
          [sessionAValue]="session?.value ?? query"
          (closePanel)="closeCompare()"
        ></app-diff-input-modal>
      }
      @if (cameFromDiff) {
        <!--
          Diff deep-link banner. Shown whenever the URL carries
          ?from=diff (set by the /diff page's "open" links). Surfaces
          the originating leaf path + a back link so the operator
          always knows where they came from.
        -->
        <div
          class="bg-amber-50 border border-amber-200 px-3 py-2 flex items-center gap-2 text-sm flex-wrap"
        >
          <i class="ri-git-compare-line text-amber-700"></i>
          <span class="text-amber-900">
            Opened from diff
            @if (diffOtherSession) {
              against
              <a
                [routerLink]="['/search']"
                [queryParams]="{ q: diffOtherSession }"
                class="font-mono text-xs underline"
              >
                {{ diffOtherSession }}
              </a>
            }
            — focused leaf
            <code class="font-mono text-xs bg-amber-100 px-1 py-0.5 rounded">
              {{ diffSourcePath }}
            </code>
            in
            <code class="font-mono text-xs">{{ diffSourcePayload }}</code>
            .
          </span>
          @if (diffOtherSession) {
            <a
              [routerLink]="['/diff']"
              [queryParams]="{ a: session?.value ?? query, b: diffOtherSession }"
              class="ml-auto text-xs px-2 py-1 border border-amber-300 bg-white text-amber-800 rounded inline-flex items-center gap-1 hover:bg-amber-100"
            >
              <i class="ri-arrow-left-line"></i>
              Back to diff
            </a>
          }
        </div>
      }
      <div class="flex flex-col px-3 py-2 gap-2">
        <!--
          Top section — split into TWO cards so the user can tell at a
          glance which fields belong to the session (context, doesn't
          change as they click between requests) and which belong to
          the selected request (changes on every click). Operator
          feedback: the previous single 3-col grid mixed both, so
          fields like Method/Consumer/Agent looked like they applied
          to "the session" when they actually only describe the one
          request currently selected.

            1. Session strip — slim horizontal row, gray bg, inline
               pills. Carries Session ID, started/last-activity,
               request count, and the "important" star (moved here
               from the left rail; session-scoped controls live with
               session metadata).

            2. Selected-request card — full white card with header
               band + status pill + 4-col grid of per-request fields.
               Status pill colour comes from getStatus → StatusInfo
               (same scale used in the left rail and the home
               table).
        -->

        <!-- ─── Order-reference match banner ───
             Shown only when the search term was an AGW order id or a
             PNR that appears in more than one session. One order
             collects a session per later OrderRetrieve / OrderChange,
             and the view can only show one at a time — the newest —
             so without this the other hits are invisible. -->
        @if (matchedSessions.length > 1) {
          <div
            class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900"
          >
            <div class="flex items-center gap-1.5 font-medium">
              <i class="ri-information-line"></i>
              <span>
                <code class="font-mono">{{ query }}</code>
                appears in {{ matchedSessions.length }} sessions — showing the most recent.
              </span>
            </div>
            <div class="mt-1 flex flex-wrap gap-x-3 gap-y-1">
              @for (match of matchedSessions; track match.value) {
                <a
                  class="font-mono hover:underline"
                  [class.text-primary]="match.value !== session?.value"
                  [class.font-semibold]="match.value === session?.value"
                  [class.text-amber-900]="match.value === session?.value"
                  [routerLink]="['/search']"
                  [queryParams]="{ q: match.value }"
                  [title]="match.updated_at"
                >
                  {{ match.value }}
                </a>
              }
            </div>
          </div>
        }

        <!-- ─── Session strip ─── -->
        <div
          class="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-x-6 gap-y-1 flex-wrap"
        >
          <div class="flex items-center gap-1.5 text-xs">
            <i class="ri-folder-2-line text-gray-500"></i>
            <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-600">
              Session
            </span>
          </div>
          <div class="flex items-baseline gap-1.5">
            <span class="text-[11px] uppercase tracking-wider text-gray-500">ID</span>
            <a
              class="text-sm font-mono text-primary hover:underline truncate max-w-[28rem]"
              [routerLink]="['/search']"
              [queryParams]="{ q: session?.value }"
              (click)="$event.stopPropagation()"
            >
              {{ session?.value }}
            </a>
          </div>
          @if (session?.created_at) {
            <div class="flex items-baseline gap-1.5">
              <span class="text-[11px] uppercase tracking-wider text-gray-500">Started</span>
              <span class="text-sm text-gray-900">
                {{ session.created_at | formatDate: 'request' }}
              </span>
            </div>
          }
          @if (session | sessionLastAction; as lastActivity) {
            <div class="flex items-baseline gap-1.5">
              <span class="text-[11px] uppercase tracking-wider text-gray-500">Last activity</span>
              <span class="text-sm text-gray-900">{{ lastActivity | formatDate: 'request' }}</span>
            </div>
          }
          <div class="flex items-baseline gap-1.5">
            <span class="text-[11px] uppercase tracking-wider text-gray-500">Requests</span>
            <span class="text-sm font-mono tabular-nums text-gray-900">{{ requests.length }}</span>
          </div>

          <!-- Status aggregate pill mirrors the home table's session-row tint -->
          @if (session | getSessionStatus; as sessStatus) {
            <span
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
              [ngClass]="sessStatus.pill"
            >
              {{ sessStatus.label }}
            </span>
          }

          @if (session) {
            <button
              type="button"
              class="ml-auto p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              [class.text-yellow-500]="session.important"
              [class.text-gray-400]="!session.important"
              [disabled]="importantPending"
              [title]="session.important ? 'Mark as not important' : 'Mark as important'"
              (click)="changeIsImportant()"
            >
              <i [ngClass]="session.important ? 'ri-star-fill' : 'ri-star-line'"></i>
            </button>
          }
        </div>

        <!-- ─── Selected request card ─── -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div
            class="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between"
          >
            <div
              class="text-[11px] font-semibold uppercase tracking-wider text-gray-600 flex items-center gap-1.5"
            >
              <i class="ri-arrow-right-line text-gray-500"></i>
              Selected request
            </div>
            @if (selectedReq | getStatus; as reqStatus) {
              <span
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                [ngClass]="reqStatus.pill"
              >
                {{ reqStatus.label }}
              </span>
            }
          </div>
          <div class="p-3">
            <div class="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
              <div>
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                  Request ID
                </div>
                <a
                  [routerLink]="['/search']"
                  [queryParams]="{ q: selectedReq.request_id }"
                  (click)="$event.stopPropagation()"
                  class="text-sm font-mono text-primary hover:underline truncate block"
                >
                  {{ selectedReq.request_id }}
                </a>
              </div>
              <div>
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">When</div>
                <div class="text-sm">{{ selectedReq.created_at | formatDate: 'request' }}</div>
              </div>
              <div>
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Method</div>
                <div class="text-sm">{{ selectedReq.method }}</div>
              </div>
              <div>
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                  Provider method
                </div>
                <div class="text-sm">{{ selectedReq.provider_method || selectedReq.method }}</div>
              </div>

              <div class="min-w-0">
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Provider</div>
                <!--
                  Use the same collapsing rule as Trace.providers
                  everywhere else in the app: a comma-list or "*"
                  collapses to "*", single providers render as-is.
                  The full original string (verbatim from
                  request.provider) is exposed via title= so hovering
                  the cell reveals the comma-list. min-w-0 + truncate
                  on the value div clamps the rendered text to the
                  grid cell width — long single-value cases (e.g. an
                  unusual provider code) get an ellipsis instead of
                  overflowing into the App column.
                -->
                <div class="text-sm truncate" [title]="selectedReq.provider || ''">
                  {{ (selectedReq.provider | collapseProviders) || '—' }}
                </div>
              </div>
              <div>
                <!--
                  App = AGW App TextID (e.g. "BookingPad", "AirGatewayPortal").
                  Resolved by ndc-json-api's InsertHubObjectsIntoContext from
                  the API key / JWT, echoed back as Ag-App response header by
                  EchoResolvedHeaders middleware (ndc-json-api MR !3503), and
                  read by trace-masker convert.go (tracer-sidecar MR !10).
                  Stored in `requests.application` — max 32 chars, sourced
                  from Hub.
                -->
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">App</div>
                <div class="text-sm truncate">{{ selectedReq.application || '—' }}</div>
              </div>
              @if (agRealm === 'admins' && hash) {
                <div>
                  <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Commit</div>
                  <div class="text-sm font-mono truncate" [title]="hash">{{ hash }}</div>
                </div>
              } @else {
                <div></div>
              }
              <div></div>

              <div>
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Consumer</div>
                <div class="text-sm truncate">{{ selectedReq.consumer }}</div>
              </div>
              <div>
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Agency</div>
                <div class="text-sm truncate">{{ selectedReq.agency }}</div>
              </div>
              <div class="md:col-span-2">
                <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Agent</div>
                <div class="text-sm truncate">{{ selectedReq.agent }}</div>
              </div>
            </div>
          </div>
        </div>
        @if (selectedTrace) {
          <div class="bg-white rounded-lg shadow-sm p-3">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center space-x-4">
                <div>
                  <div class="text-sm text-gray-500 mb-1">Providers</div>
                  <div class="text-sm" [title]="selectedTrace.providers">
                    {{ selectedTrace.providers | collapseProviders }}
                  </div>
                </div>
                <!--
                  Per-call method of the selected requester trace (tracer
                  migration 75). Identifies WHICH outbound call of a
                  multi-step flow is being inspected — sibling traces share
                  providers, so this is the disambiguator.
                -->
                @if (selectedTrace.method) {
                  <div>
                    <div class="text-sm text-gray-500 mb-1">Method</div>
                    <div class="text-sm">{{ selectedTrace.method }}</div>
                  </div>
                }
                <div>
                  <div class="text-sm text-gray-500 mb-1">Status</div>
                  <div class="text-sm">
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      [ngClass]="
                        selectedTrace.status === 'ok'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      "
                    >
                      {{ selectedTrace.status }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="border border-gray-200 rounded-lg overflow-hidden">
              <div class="p-2 bg-gray-50">
                <div class="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <div class="col-span-1">
                    @if (
                      (
                        selectedReq.traces
                        | getTracesBySource
                          : uniqueTracesByProviders
                          : shouldTracesBeGrouped
                          : 'json-api'
                      )?.length
                    ) {
                      <div class="text-xs text-gray-500 mb-1">JSON-API</div>
                      @for (
                        trace of selectedReq.traces
                          | getTracesBySource
                            : uniqueTracesByProviders
                            : shouldTracesBeGrouped
                            : 'json-api';
                        track trace.id
                      ) {
                        <div
                          class="text-center cursor-pointer px-2 py-1 rounded text-sm mb-1 border border-gray-200 hover:bg-gray-200 truncate"
                          [title]="trace.providers"
                          (click)="selectTrace(trace)"
                          [ngClass]="
                            trace.id === selectedTrace?.id
                              ? 'bg-gray-200 text-gray-700'
                              : 'bg-white'
                          "
                        >
                          {{ trace.providers | collapseProviders }}
                        </div>
                      }
                    }
                    @if (
                      (
                        selectedReq.traces
                        | getTracesBySource
                          : uniqueTracesByProviders
                          : shouldTracesBeGrouped
                          : 'requester'
                      )?.length
                    ) {
                      <div class="text-xs text-gray-500 mt-2 mb-1">REQUESTER</div>
                      @if (shouldTracesBeGrouped) {
                        @for (
                          trace of selectedReq.traces
                            | getTracesBySource
                              : uniqueTracesByProviders
                              : shouldTracesBeGrouped
                              : 'requester';
                          track trace.id
                        ) {
                          @let groupTraces =
                            selectedReq.traces
                              | getTracesBySourceAndProviders: 'requester' : trace.providers;
                          @let isMulti = groupTraces.length > 1;
                          <div
                            class="rounded border mb-1"
                            [ngClass]="
                              isMulti ? 'border-gray-300 bg-gray-50/50' : 'border-gray-200'
                            "
                          >
                            <button
                              class="flex items-center justify-between gap-2 cursor-pointer border px-2 py-1 rounded text-sm w-full"
                              [ngClass]="
                                (trace.id === selectedTrace?.id && !isMulti) ||
                                expandedGroups.has(trace.id)
                                  ? 'bg-gray-200 text-gray-700'
                                  : 'bg-white'
                              "
                              [class]="trace | getTraceBorderColor: selectedReq.traces"
                              (click)="
                                trace.providers !== selectedTrace?.providers ||
                                trace.source !== selectedTrace?.source
                                  ? selectTrace(trace)
                                  : null
                              "
                              (click)="toggleGroup(trace.id)"
                            >
                              <span class="truncate flex items-center gap-1.5 min-w-0">
                                <!--
                                  Single (non-parallel) requests are never tied
                                  to a specific cabin/fare, so we show only the
                                  provider here. The cabin/fare Label is only
                                  meaningful to distinguish parallel siblings,
                                  and is rendered per-call in the expanded
                                  "Parallel requests" sub-list below (isMulti).
                                -->
                                <span class="truncate">
                                  {{ trace.providers | collapseProviders }}
                                </span>
                              </span>
                              @if (isMulti) {
                                <span
                                  class="inline-flex items-center gap-1 text-xs text-gray-500 flex-shrink-0"
                                  [title]="
                                    groupTraces.length + ' parallel requests to this provider'
                                  "
                                >
                                  <span
                                    class="px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700 font-mono tabular-nums"
                                  >
                                    ×{{ groupTraces.length }}
                                  </span>
                                  <i
                                    class="transition-transform duration-150"
                                    [ngClass]="
                                      expandedGroups.has(trace.id)
                                        ? 'ri-arrow-down-s-line'
                                        : 'ri-arrow-right-s-line'
                                    "
                                  ></i>
                                </span>
                              }
                            </button>

                            @if (
                              isMulti &&
                              trace.source === selectedTrace?.source &&
                              expandedGroups.has(trace.id)
                            ) {
                              <div class="relative pl-4 pr-1 pb-1 pt-1 space-y-0.5">
                                <!-- Section header: clarifies the nesting semantic -->
                                <div
                                  class="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1 ml-2"
                                >
                                  Parallel requests
                                </div>
                                <!-- Vertical connector line for visual nesting -->
                                <div class="absolute left-2 top-5 bottom-1 w-px bg-gray-300"></div>
                                @for (
                                  innerTrace of groupTraces;
                                  track innerTrace.id;
                                  let i = $index
                                ) {
                                  <button
                                    class="relative flex items-center gap-2 cursor-pointer px-2 py-1 rounded text-xs border border-gray-200 hover:bg-gray-100 w-full"
                                    [ngClass]="{
                                      'bg-gray-200 text-gray-700':
                                        innerTrace.id === selectedTrace?.id,
                                      'bg-white': innerTrace.id !== selectedTrace?.id,
                                      'border-2 border-b-primary': innerTrace.status === 'error',
                                    }"
                                    (click)="selectTrace(innerTrace); $event.preventDefault()"
                                  >
                                    <span
                                      class="inline-block font-mono tabular-nums text-gray-400 w-6 text-right"
                                    >
                                      #{{ i + 1 }}
                                    </span>
                                    <span class="truncate flex-1 text-left text-gray-700">
                                      {{ innerTrace.providers | collapseProviders }}
                                      @if (innerTrace.label) {
                                        <span class="text-gray-500">· {{ innerTrace.label }}</span>
                                      }
                                      <!--
                                        Sequential sub-call discriminator (see
                                        the flat requester list below): a group
                                        can contain a sub-call whose method
                                        differs from the request's (e.g. an
                                        OrderReshop group holding a reprice
                                        step since tracer migration 75).
                                      -->
                                      @if (
                                        innerTrace.method &&
                                        innerTrace.method !== selectedReq.method
                                      ) {
                                        <span class="text-gray-500">· {{ innerTrace.method }}</span>
                                      }
                                    </span>
                                    @if (innerTrace.status === 'error') {
                                      <i
                                        class="ri-error-warning-line text-primary flex-shrink-0"
                                        title="Error response"
                                      ></i>
                                    }
                                  </button>
                                }
                              </div>
                            }
                          </div>
                        }
                      } @else {
                        @for (
                          trace of selectedReq.traces
                            | getTracesBySource
                              : uniqueTracesByProviders
                              : shouldTracesBeGrouped
                              : 'requester';
                          track trace.id
                        ) {
                          <div
                            class="text-center border border-gray-200 px-2 py-1 mb-1 rounded text-sm cursor-pointer hover:bg-gray-200"
                            [ngClass]="
                              trace.id === selectedTrace?.id
                                ? 'bg-gray-200 text-gray-700'
                                : 'bg-white'
                            "
                            (click)="selectTrace(trace)"
                          >
                            {{ trace.providers | collapseProviders }}
                            <!--
                              Per-call method suffix. One inbound request can
                              fire SEQUENTIAL calls to the same provider
                              (OrderCreate → OrderReserve, reprice → change);
                              since tracer migration 75 each lands on its own
                              trace, so sibling chips here can share providers
                              and need the method to be told apart. Shown only
                              when it differs from the request's own method,
                              so the common single-call case stays clean.
                            -->
                            @if (trace.method && trace.method !== selectedReq.method) {
                              <span class="text-gray-500">· {{ trace.method }}</span>
                            }
                          </div>
                        }
                      }
                    }
                  </div>
                  <div class="col-span-1 md:col-span-5 mt-4 md:mt-0">
                    <div
                      class="flex gap-2 border border-gray-200 rounded overflow-hidden mb-2 md:mt-[12px]"
                    >
                      @for (message of selectedTrace.messages | orderMessages; track message.id) {
                        <button
                          class="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none whitespace-nowrap !rounded-button"
                          [ngClass]="
                            message.id === selectedMessage?.id ? 'bg-gray-200' : 'bg-white'
                          "
                          (click)="selectMessage(message)"
                        >
                          {{ message.name | messageTabLabel: selectedReq.method }}
                        </button>
                      }
                    </div>

                    @if (selectedMessage && headers) {
                      <div class="flex flex-wrap gap-3 justify-between items-center mb-2">
                        <button
                          class="text-primary hover:underline text-sm flex items-center whitespace-nowrap !rounded-button"
                          (click)="showHeaders = !showHeaders"
                        >
                          <i class="ri-eye-line mr-1"></i>
                          Headers: {{ showHeaders ? 'hide' : 'show' }}
                        </button>
                        <div class="flex space-x-2">
                          @if (
                            (selectedTrace?.source === 'json-api' ||
                              selectedTrace?.source === 'sidecar') &&
                            selectedMessage?.name === 'input'
                          ) {
                            <button class="btn btn-sm btn-outline btn-info" (click)="copyCurl()">
                              <i class="ri-link-m mr-1"></i>
                              {{ copiedCurl ? 'Copied!' : 'Copy cURL' }}
                            </button>
                          }
                          <button class="btn btn-sm btn-outline btn-info" (click)="copyHeaders()">
                            <i class="ri-file-copy-line mr-1"></i>
                            {{ copiedHeaders ? 'Copied!' : 'Copy headers' }}
                          </button>
                        </div>
                      </div>
                      @if (showHeaders) {
                        <ul
                          class="text-sm font-mono space-y-0.5 bg-white rounded p-2 mb-2 break-words whitespace-pre-wrap"
                        >
                          @for (header of headers | keyvalue; track header.key) {
                            <li class="break-words whitespace-pre-wrap">
                              {{ header.key }}: {{ header.value }}
                            </li>
                          }
                        </ul>
                      }
                    }

                    <div class="flex flex-wrap gap-3 justify-between items-center mb-4">
                      <div class="text-sm text-gray-600">
                        {{ selectedMessage?.created_at | formatDate: 'message' }}
                      </div>
                      <div class="flex space-x-2">
                        <button class="btn btn-sm btn-outline btn-info" (click)="copyCode()">
                          <i class="ri-code-s-slash-line mr-1"></i>
                          {{ copiedBody ? 'Copied!' : 'Copy code' }}
                        </button>
                        @if (!showFetchMessageDetailsButton && showDownloadButton) {
                          <button class="btn btn-sm btn-outline btn-info" (click)="downloadFile()">
                            <i class="ri-download-line mr-1"></i>
                            Download File
                          </button>
                        }
                      </div>
                    </div>

                    <div
                      class="bg-gray-100 p-2 rounded-lg font-mono text-sm max-h-[90vh] overflow-x-auto"
                    >
                      @if (isMessageLoading && !body) {
                        <div class="relative min-h-[60px]">
                          <ngx-spinner
                            size="medium"
                            color="#cd0005"
                            bdColor="transparent"
                            [fullScreen]="false"
                          ></ngx-spinner>
                        </div>
                      } @else {
                        @if (body && selectedMessage) {
                          @if (selectedMessage?.format === 'json' && (body | isValidJson)) {
                            <ngx-json-viewer [json]="body | parseJson"></ngx-json-viewer>
                          } @else {
                            <pre><code [highlight]="body" [language]="selectedMessage.format | normalizeFormat"></code></pre>
                          }
                        }
                        @if (
                          showFetchMessageDetailsButton ||
                          fetchMessageDetailsWarning ||
                          fetchMessageDetailsError
                        ) {
                          <div class="text-center mt-3">
                            @if (showFetchMessageDetailsButton) {
                              <button
                                class="btn btn-sm btn-primary"
                                (click)="fetchMessageDetails()"
                              >
                                SHOW FILE CONTENT HERE
                              </button>
                            }

                            @if (fetchMessageDetailsWarning) {
                              <!--
                                Three-tier file-size UI (backed by
                                page_controller.go's inlineDisplayLimit /
                                onDemandDisplayLimit):
                                  Tier 1 ≤ 250 KB  → no warning, body shown
                                  Tier 2 ≤ 1 MB    → warning + "Show file" button
                                                     (onDemandPresignedURL set)
                                  Tier 3 > 1 MB    → warning only, NO button
                                                     (selectedMessageZipOnly=true)
                              -->
                              <div role="alert" class="alert alert-warning flex-col items-stretch">
                                <div class="flex items-start gap-2">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-6 w-6 shrink-0 stroke-current"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      stroke-linecap="round"
                                      stroke-linejoin="round"
                                      stroke-width="2"
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                  </svg>
                                  <span>{{ fetchMessageDetailsWarning }}</span>
                                </div>
                                @if (onDemandPresignedURL) {
                                  <button
                                    class="btn btn-sm btn-primary mt-2 self-center"
                                    (click)="loadOnDemandFile()"
                                  >
                                    Show file ({{ humanBytes(selectedMessageSize) }})
                                  </button>
                                }
                              </div>
                            }

                            @if (fetchMessageDetailsError) {
                              <div role="alert" class="alert alert-error">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  class="h-6 w-6 shrink-0 stroke-current"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span>{{ fetchMessageDetailsError }}</span>
                              </div>
                            }
                          </div>
                        }
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        } @else if (selectedReq) {
          <!--
            Diagnostic empty-state: the request EXISTS in the response
            but no trace got selected. Two causes that look identical
            from the operator's perspective without this panel:
              (a) the request truly has 0 traces in the database — a
                  capture was attempted but no trace row landed (e.g.
                  the in-handler emit fired before the body-write
                  completed and the row got rolled back).
              (b) the request has traces but they were filtered out
                  by the backend's role-based projection — most
                  commonly an agent viewing a session whose only
                  traces are source='requester' (admin-only) or were
                  excluded by a stale source filter (the bug fixed in
                  tracer MR !393 for the sidecar source).

            Surface the request-level facts we DO have so the operator
            can act on it (re-fire the call, ask an admin, file a
            bug). Better than the previous behaviour of rendering a
            blank canvas below the metadata card.
          -->
          <div class="bg-white rounded-lg shadow-sm p-4">
            <div class="flex items-start gap-3">
              <i class="ri-error-warning-line text-yellow-500 text-2xl mt-0.5"></i>
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-semibold text-gray-800">
                  Trace data unavailable for this request
                </h3>
                <p class="mt-1 text-xs text-gray-600">
                  The request was captured but no trace data is visible to your account.
                  @if (!selectedReq.traces || selectedReq.traces.length === 0) {
                    The backend returned
                    <strong>0 traces</strong>
                    for this request — a capture was attempted but the trace rows didn't land, or
                    your role filters them all out (e.g. agent users don't see
                    <code>requester</code>
                    -source traces).
                  } @else {
                    The backend returned
                    <strong>{{ selectedReq.traces.length }}</strong>
                    trace(s) but none of them appear inbound. This usually means the only traces are
                    <code>requester</code>
                    -source (visible to admins only). Ask an admin to re-open this URL.
                  }
                </p>
                <div class="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div>
                    <div class="text-gray-500">Method</div>
                    <div class="font-mono">{{ selectedReq.method || '—' }}</div>
                  </div>
                  <div>
                    <div class="text-gray-500">Provider</div>
                    <div class="font-mono">
                      {{ selectedReq.provider || '—' }}
                    </div>
                  </div>
                  <div>
                    <div class="text-gray-500">Last status</div>
                    <div class="font-mono">{{ selectedReq.status || '—' }}</div>
                  </div>
                </div>
                @if (selectedReq.traces && selectedReq.traces.length > 0) {
                  <!--
                    Surface the trace rows we DO have, even when none
                    were auto-selected. Lets an admin (who sees all
                    sources via /session) click into a requester
                    trace; lets an agent at least see the count and
                    sources so the failure mode is concrete.
                  -->
                  <div class="mt-3">
                    <div class="text-xs text-gray-500 mb-1">
                      Traces present in the response (click to inspect):
                    </div>
                    <div class="flex flex-wrap gap-1">
                      @for (trace of selectedReq.traces; track trace.id) {
                        <button
                          type="button"
                          class="px-2 py-1 rounded text-xs border border-gray-200 bg-gray-50 hover:bg-gray-100 font-mono"
                          [title]="
                            'source=' +
                            trace.source +
                            ' providers=' +
                            (trace.providers || '—') +
                            ' status=' +
                            (trace.status || '—')
                          "
                          (click)="selectTrace(trace)"
                        >
                          {{ trace.source }} · {{ trace.providers || '—' }} ·
                          <span
                            [ngClass]="trace.status === 'ok' ? 'text-green-700' : 'text-red-700'"
                          >
                            {{ trace.status || '?' }}
                          </span>
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
} @else if (loaded && !selectedReq) {
  <!-- Empty / not-found state. Reached when getSessionData completed
       but the response had no requests (BE returned `error:` payload,
       or requests array empty). Previously the component redirected to
       / on this branch, which hid the URL and the BE error message;
       now we keep the user on /search and show a clear empty state so
       they can see what they searched for and copy/retry. -->
  <div class="flex flex-1 items-center justify-center p-6">
    <div
      class="max-w-lg w-full text-center bg-white border border-gray-200 rounded-lg shadow-sm p-6"
    >
      <i class="ri-search-line text-4xl text-gray-400"></i>
      <h2 class="mt-3 text-lg font-semibold text-gray-700">No session found</h2>
      <p class="mt-1 text-sm text-gray-500">
        We couldn't find any requests for
        <code class="font-mono text-gray-700 break-all">{{ query }}</code>
        .
      </p>
      <p class="mt-1 text-xs text-gray-400">
        The session may have been purged by retention, the ID may be from a different environment,
        or the lookup may not match a Request ID, Session ID, AGW Order ID or PNR. Order IDs and
        PNRs are only searchable for orders captured after the feature shipped — older traces are
        still reachable by Request or Session ID.
      </p>
      <a [routerLink]="['/']" class="btn btn-sm btn-primary mt-4">Back to home</a>
    </div>
  </div>
} @else {
  <ngx-spinner color="#cd0005"></ngx-spinner>
}
